import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  useTheme
} from '@mui/material';
import {
  LocationCityOutlined as LocationCityOutlinedIcon,
  Search as SearchIcon,
  Layers as LayersIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Import API client
import apiClient from '../../utils/apiClient';
import { handleApiError } from '../../utils/apiService';
import { useSnackbar } from 'notistack';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// API service for the TX District Tool (formerly named "City Explorer")
const TxDistrictAPI = {
  getTexasDistricts: async (searchTerm = '') => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    
    const endpoint = `/v2/tx-district-tool/districts/texas${params.toString() ? '?' + params.toString() : ''}`;
    return await apiClient.get(endpoint);
  },
  
  getDistrictsList: async () => {
    return await apiClient.get('/v2/tx-district-tool/districts/texas/list');
  },
  
  getDistrictByName: async (districtName) => {
    return await apiClient.get(`/v2/tx-district-tool/districts/texas/${encodeURIComponent(districtName)}`);
  },
  
  getStatus: async () => {
    return await apiClient.get('/v2/tx-district-tool/status');
  }
};

// Component to fit map bounds to GeoJSON
const FitBounds = ({ geoJsonData }) => {
  const map = useMap();
  
  useEffect(() => {
    if (geoJsonData && geoJsonData.features && geoJsonData.features.length > 0) {
      const geoJsonLayer = L.geoJSON(geoJsonData);
      const bounds = geoJsonLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [geoJsonData, map]);
  
  return null;
};

const TxDistrictExplorer = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [districtsList, setDistrictsList] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [mapKey, setMapKey] = useState(0); // Force map re-render
  const [showAllDistricts, setShowAllDistricts] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [hiddenDistricts, setHiddenDistricts] = useState(new Set()); // Track hidden districts
  
  const mapRef = useRef();

  // Define Houston ISD bordering districts
  const houstonBorderingDistricts = new Set([
    'Alief Independent School District',
    'Katy Independent School District', 
    'Aldine Independent School District',
    'Spring Independent School District',
    'Spring Branch Independent School District',
    'Cypress-Fairbanks Independent School District',
    'Galena Park Independent School District',
    'Fort Bend Independent School District',
    'Pasadena Independent School District',
    'Humble Independent School District',
    'Sheldon Independent School District',
    'Alvin Independent School District',
    'Pearland Independent School District',
    'Stafford Municipal School District'
  ]);

  const loadStatus = useCallback(async () => {
    try {
      const statusData = await TxDistrictAPI.getStatus();
      setStatus(statusData);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error loading status:', err);
      handleApiError(err, enqueueSnackbar);
      setError('Failed to load TX District Tool status. Please ensure the backend server is running.');
    }
  }, [enqueueSnackbar]);

  const loadDistrictsList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await TxDistrictAPI.getDistrictsList();
      setDistrictsList(data.districts || []);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error loading districts list:', err);
      handleApiError(err, enqueueSnackbar);
      setError('Failed to load districts list. Please ensure the backend server is running.');
      setDistrictsList([]); // Set empty list on error
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadAllDistricts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await TxDistrictAPI.getTexasDistricts();
      setGeoJsonData(data);
      setSelectedFeatures([]);
      setMapKey(prev => prev + 1); // Force map re-render
    } catch (err) {
      console.error('Error loading all districts:', err);
      handleApiError(err, enqueueSnackbar);
      setError('Failed to load district data. Please ensure the backend server is running.');
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadDistrictByName = useCallback(async (districtName) => {
    if (!districtName) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await TxDistrictAPI.getDistrictByName(districtName);
      setGeoJsonData(data);
      setSelectedFeatures(data.features || []);
      setMapKey(prev => prev + 1); // Force map re-render
    } catch (err) {
      console.error('Error loading district:', err);
      handleApiError(err, enqueueSnackbar);
      setError(`Failed to load district "${districtName}". Please ensure the backend server is running.`);
      setGeoJsonData(null); // Clear data on error
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      try {
        await loadStatus();
        await loadDistrictsList();
        // Try to load Houston ISD by default
        await loadDistrictByName('Houston Independent School District');
      } catch (err) {
        // Errors are already handled in individual functions
        console.log('Initial data loading completed with errors');
      }
    };
    
    initializeData();
  }, [loadStatus, loadDistrictsList, loadDistrictByName]);

  const handleDistrictSelect = (event, district) => {
    setSelectedDistrict(district);
    if (district) {
      loadDistrictByName(district.name);
      setShowAllDistricts(false);
    }
  };

  const handleShowAllToggle = (event) => {
    const showAll = event.target.checked;
    setShowAllDistricts(showAll);
    
    // Clear hidden districts when switching modes
    setHiddenDistricts(new Set());
    
    if (showAll) {
      loadAllDistricts();
      setSelectedDistrict(null);
    } else {
      // If we had a selected district, reload it
      if (selectedDistrict) {
        loadDistrictByName(selectedDistrict.name);
      }
    }
  };

  const getGeoJsonStyle = (feature) => {
    const isSelected = selectedFeatures.some(f => f.properties.GEOID === feature.properties.GEOID);
    const isHoustonISD = feature.properties.NAME === 'Houston Independent School District';
    const isBorderingDistrict = houstonBorderingDistricts.has(feature.properties.NAME);
    const isHidden = hiddenDistricts.has(feature.properties.GEOID);
    
    // Hidden districts are transparent/faded
    if (isHidden) {
      return {
        fillColor: '#CCCCCC',
        weight: 1,
        opacity: 0.3,
        color: '#999999',
        dashArray: '5, 5', // Dashed border for hidden
        fillOpacity: 0.1
      };
    }
    
    // When showing all districts, categorize by relationship to Houston ISD
    if (showAllDistricts) {
      if (isHoustonISD) {
        // Houston ISD - bright orange
        return {
          fillColor: '#FF6B35',
          weight: 3,
          opacity: 1,
          color: '#CC4125',
          dashArray: '',
          fillOpacity: 0.8
        };
      } else if (isBorderingDistrict) {
        // Bordering districts - green
        return {
          fillColor: '#4CAF50', // Green for bordering districts
          weight: 2,
          opacity: 1,
          color: '#2E7D32', // Darker green border
          dashArray: '',
          fillOpacity: 0.7
        };
      } else {
        // Extended metro districts - light blue
        return {
          fillColor: '#81C784', // Light green for extended districts
          weight: 1,
          opacity: 1,
          color: '#66BB6A',
          dashArray: '',
          fillOpacity: 0.5
        };
      }
    }
    
    // Single district view (original logic)
    return {
      fillColor: isSelected ? theme.palette.primary.main : theme.palette.secondary.light,
      weight: isSelected ? 3 : 1,
      opacity: 1,
      color: isSelected ? theme.palette.primary.dark : theme.palette.grey[400],
      dashArray: '',
      fillOpacity: isSelected ? 0.7 : 0.3
    };
  };

  const toggleDistrictVisibility = (geoid, districtName) => {
    setHiddenDistricts(prev => {
      const newHidden = new Set(prev);
      if (newHidden.has(geoid)) {
        newHidden.delete(geoid);
        enqueueSnackbar(`✅ ${districtName} is now visible`, { variant: 'success' });
      } else {
        newHidden.add(geoid);
        enqueueSnackbar(`👁️‍🗨️ ${districtName} is now hidden`, { variant: 'info' });
      }
      return newHidden;
    });
    
    // Force map re-render to apply new styles
    setMapKey(prev => prev + 1);
  };

  const onEachFeature = (feature, layer) => {
    const districtName = feature.properties.NAME || 'Unknown District';
    const isHoustonISD = districtName === 'Houston Independent School District';
    const isBorderingDistrict = houstonBorderingDistricts.has(districtName);
    const isHidden = hiddenDistricts.has(feature.properties.GEOID);
    
    // Determine district category for display
    let categoryInfo = '';
    let categoryColor = theme.palette.info.main;
    if (isHoustonISD) {
      categoryInfo = '<span style="background: #FF6B35; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold;">HOUSTON ISD</span>';
      categoryColor = '#FF6B35';
    } else if (isBorderingDistrict) {
      categoryInfo = '<span style="background: #4CAF50; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold;">BORDERING</span>';
      categoryColor = '#4CAF50';
    } else if (showAllDistricts) {
      categoryInfo = '<span style="background: #81C784; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold;">EXTENDED METRO</span>';
      categoryColor = '#81C784';
    }
    
    // Create hover tooltip
    const tooltipContent = `
      <div style="
        font-family: 'Roboto', Arial, sans-serif; 
        background: white; 
        padding: 8px 12px; 
        border-radius: 8px; 
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        border-left: 4px solid ${categoryColor};
        max-width: 250px;
        ${isHidden ? 'opacity: 0.6;' : ''}
      ">
        <h4 style="
          margin: 0 0 6px 0; 
          color: ${categoryColor}; 
          font-size: 14px;
          font-weight: 600;
          ${isHidden ? 'text-decoration: line-through;' : ''}
        ">${districtName}</h4>
        ${categoryInfo}
        ${isHidden ? '<div style="color: #666; font-size: 11px; margin-top: 4px;">👁️‍🗨️ Hidden - Click to show</div>' : ''}
        ${showAllDistricts && !isHoustonISD ? '<div style="color: #666; font-size: 11px; margin-top: 4px;">💡 Click to toggle visibility</div>' : ''}
      </div>
    `;
    
    // Create detailed popup
    const popupContent = `
      <div style="font-family: 'Roboto', Arial, sans-serif; min-width: 200px;">
        <h4 style="
          margin: 0 0 8px 0; 
          color: ${categoryColor}; 
          font-size: 16px;
          font-weight: 600;
          border-bottom: 2px solid ${categoryColor};
          padding-bottom: 4px;
        ">${districtName}</h4>
        <div style="margin: 8px 0;">${categoryInfo}</div>
        <p style="margin: 4px 0; font-size: 13px;"><strong>GEOID:</strong> ${feature.properties.GEOID || 'N/A'}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>LEA Code:</strong> ${feature.properties.UNSDLEA || feature.properties.unsdlea || 'N/A'}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>State:</strong> Texas</p>
        ${showAllDistricts ? '<p style="margin: 8px 0 0 0; font-size: 11px; color: #666; font-style: italic;">💡 Click to toggle visibility • Right-click to zoom</p>' : '<p style="margin: 8px 0 0 0; font-size: 11px; color: #666; font-style: italic;">Click to zoom to this district</p>'}
      </div>
    `;
    
    // Bind tooltip for hover
    layer.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'top',
      offset: [0, -10],
      className: 'custom-tooltip'
    });
    
    // Bind popup for click
    layer.bindPopup(popupContent);
    
    layer.on({
      mouseover: (e) => {
        if (!isHidden) {
          const currentStyle = getGeoJsonStyle(feature);
          e.target.setStyle({
            ...currentStyle,
            weight: currentStyle.weight + 2,
            fillOpacity: Math.min(currentStyle.fillOpacity + 0.2, 1.0)
          });
        }
        
        // Open tooltip
        e.target.openTooltip();
      },
      mouseout: (e) => {
        // Reset to original style
        e.target.setStyle(getGeoJsonStyle(feature));
        
        // Close tooltip
        e.target.closeTooltip();
      },
      click: (e) => {
        e.originalEvent.preventDefault();
        
        if (showAllDistricts) {
          // When showing all districts, clicking toggles visibility (except Houston ISD)
          if (isHoustonISD) {
            // Houston ISD - offer to focus on it
            setTimeout(() => {
              if (window.confirm('Focus on Houston ISD only?')) {
                setShowAllDistricts(false);
                loadDistrictByName('Houston Independent School District');
              }
            }, 100);
          } else {
            // Other districts - toggle visibility
            toggleDistrictVisibility(feature.properties.GEOID, districtName);
          }
        } else {
          // Single district view - zoom to bounds
          if (mapRef.current) {
            const bounds = e.target.getBounds();
            mapRef.current.fitBounds(bounds, { padding: [30, 30] });
          }
        }
      },
      contextmenu: (e) => {
        // Right-click always zooms to district
        e.originalEvent.preventDefault();
        if (mapRef.current) {
          const bounds = e.target.getBounds();
          mapRef.current.fitBounds(bounds, { padding: [30, 30] });
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationCityOutlinedIcon color="primary" />
          TX District Tool
          <Chip label="Beta" color="warning" size="small" />
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualize Texas school district boundaries using US Census TIGER/Line shapefiles.
        </Typography>
      </Box>

      {/* Status */}
      {status && (
        <Alert 
          severity={status.active_data_source && status.active_data_source !== 'No data available' ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          icon={<InfoIcon />}
        >
          {status.active_data_source && status.active_data_source !== 'No data available' ? (
            <>
              <strong>TX District Tool Ready:</strong> Using {status.active_data_source}
              {status.districts_count && ` (${status.districts_count} districts)`}
              {status.coverage && ` - ${status.coverage}`}
            </>
          ) : (
            'No district data available. Please check the data directory.'
          )}
        </Alert>
      )}

      {/* Backend Status Message */}
      {!status && !loading && error && error.includes('backend') && (
        <Alert 
          severity="info"
          sx={{ mb: 2 }}
          icon={<InfoIcon />}
        >
          <Typography variant="body2" component="div">
            <strong>Getting Started with the TX District Tool:</strong>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Open a terminal in the project root</li>
              <li>Run: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>./start-local-dev.sh</code></li>
              <li>Wait for "Backend server running on port 5001"</li>
              <li>Refresh this page to load district data</li>
            </ol>
          </Typography>
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LayersIcon />
            Map Controls
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {/* District Search */}
            <Autocomplete
              value={selectedDistrict}
              onChange={handleDistrictSelect}
              inputValue={searchValue}
              onInputChange={(event, newInputValue) => {
                setSearchValue(newInputValue);
              }}
              options={districtsList}
              getOptionLabel={(option) => option.name || ''}
              sx={{ minWidth: 300 }}
              disabled={showAllDistricts || loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Districts"
                  placeholder="Type to search districts..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.geoid}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      GEOID: {option.geoid}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {/* Show All Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={showAllDistricts}
                  onChange={handleShowAllToggle}
                  disabled={loading}
                />
              }
              label="Show All Districts"
            />

            {/* Quick Actions */}
            <Button
              variant="outlined"
              onClick={() => loadDistrictByName('Houston Independent School District')}
              disabled={loading || showAllDistricts}
              startIcon={<LocationOnIcon />}
            >
              Houston ISD
            </Button>

            {/* Reset Hidden Districts */}
            {showAllDistricts && hiddenDistricts.size > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setHiddenDistricts(new Set());
                  setMapKey(prev => prev + 1);
                  enqueueSnackbar('All districts are now visible', { variant: 'success' });
                }}
                sx={{ color: theme.palette.grey[600] }}
              >
                Show All ({hiddenDistricts.size} hidden)
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Legend */}
      {showAllDistricts && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationCityOutlinedIcon fontSize="small" />
              District Legend
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  backgroundColor: '#FF6B35', 
                  border: '2px solid #CC4125',
                  borderRadius: '4px'
                }} />
                <Typography variant="body2">Houston ISD</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  backgroundColor: '#4CAF50', 
                  border: '2px solid #2E7D32',
                  borderRadius: '4px'
                }} />
                <Typography variant="body2">Bordering Districts ({Array.from(houstonBorderingDistricts).filter(name => 
                  districtsList.some(d => d.name === name)
                ).length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  backgroundColor: '#81C784', 
                  border: '2px solid #66BB6A',
                  borderRadius: '4px'
                }} />
                <Typography variant="body2">Extended Metro</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  backgroundColor: '#CCCCCC', 
                  border: '2px dashed #999999',
                  borderRadius: '4px'
                }} />
                <Typography variant="body2">Hidden Districts</Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              💡 Click any district (except Houston ISD) to toggle visibility • Right-click to zoom to district
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Map */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            District Boundaries
          </Typography>
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading district data...</Typography>
            </Box>
          )}
          
          <Box sx={{ height: 600, position: 'relative' }}>
            {!loading && !geoJsonData && error?.includes('backend') ? (
              <Box 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  border: '2px dashed #ccc',
                  borderRadius: 1
                }}
              >
                <Typography variant="h6" color="text.secondary" textAlign="center">
                  🗺️ Start the backend server to view district maps
                </Typography>
              </Box>
            ) : (
              <MapContainer
                key={mapKey}
                ref={mapRef}
                center={[29.7604, -95.3698]} // Houston coordinates
                zoom={10}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                />
                
                {geoJsonData && geoJsonData.features && (
                  <>
                    <GeoJSON
                      key={JSON.stringify(geoJsonData)}
                      data={geoJsonData}
                      style={getGeoJsonStyle}
                      onEachFeature={onEachFeature}
                    />
                    <FitBounds geoJsonData={geoJsonData} />
                  </>
                )}
              </MapContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Metadata */}
      {geoJsonData && geoJsonData.metadata && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Data Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {geoJsonData.metadata.total_districts || geoJsonData.metadata.districts_found || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Districts Displayed
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {geoJsonData.metadata.state || 'Texas'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  State
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {geoJsonData.metadata.year || '2024'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Year
                </Typography>
              </Paper>
            </Box>
            <Typography variant="caption" display="block" sx={{ mt: 2, textAlign: 'center' }}>
              Source: {geoJsonData.metadata.source || 'US Census Bureau TIGER/Line Shapefiles'}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TxDistrictExplorer;
